import { RIYAL } from "@/lib/currency";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  MapPin,
  Building2,
  FileText,
  AlertTriangle,
  ExternalLink,
  Compass,
  Wrench,
  ShieldCheck,
  Info,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/lib/formatters";
import { getRegaSaleTotal } from "@/lib/regaPricing";
import type { ListerData, AdvertisementData, UnitInitialData } from "./types";
import type { RegaValidateResponse, RegaLocation } from "@/types/rega";
import { AdPublishBrokerageWarning } from "@/components/brokerage/BrokerageSoftWarn";

interface ReviewStepProps {
  listerData: ListerData;
  advertisementData: AdvertisementData;
  regaData: RegaValidateResponse | null;
  unitData?: UnitInitialData | null;
  propertyData?: UnitInitialData["property"] | null;
  /** Property being advertised — drives the non-blocking brokerage advisory. */
  propertyId?: string;
}

/** Renders a read-only label/value pair. Returns null if value is empty. */
function ReadOnlyField({ label, value, mono }: { label: string; value: unknown; mono?: boolean }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <span className="text-muted-foreground text-xs">{label}</span>
      <p className={`font-medium text-sm ${mono ? "font-mono" : ""}`}>{String(value)}</p>
    </div>
  );
}

export function ReviewStep({
  listerData,
  advertisementData,
  regaData,
  unitData,
  propertyData,
  propertyId,
}: ReviewStepProps) {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Combine existing and new images for the gallery
  const allImages: string[] = [
    ...advertisementData.existingImages,
    ...advertisementData.images.map(file => URL.createObjectURL(file)),
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  // Helper to get location fields safely
  const loc: RegaLocation | null =
    regaData?.location && typeof regaData.location !== "string"
      ? regaData.location
      : null;

  const getAdTypeBadge = () => {
    if (!regaData?.advertisementType) return null;
    const adType = String(regaData.advertisementType).toLowerCase();
    if (adType.includes("sell") || adType.includes("sale") || adType.includes("بيع")) {
      return <Badge className="bg-green-600">{t("بيع", "Sale")}</Badge>;
    }
    return <Badge variant="secondary">{t("إيجار", "Rent")}</Badge>;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Non-blocking brokerage advisory — full-width; collapses when no warning */}
      <div className="lg:col-span-2 empty:hidden">
        {/* Unit ads are checked at UNIT level: a property-wide contract covers
            every unit, but a contract scoped to a SIBLING unit authorises
            nothing here. Falls back to the property when the ad is not for a
            specific unit. */}
        <AdPublishBrokerageWarning
          propertyId={propertyId ?? propertyData?.id}
          unitId={unitData?.id}
        />
      </div>
      {/* Left Column */}
      <div className="space-y-4">
        {/* Image Gallery */}
        <div className="relative rounded-lg overflow-hidden bg-muted aspect-video">
          {allImages.length > 0 ? (
            <>
              <img
                src={allImages[currentImageIndex]}
                alt={advertisementData.title}
                className="w-full h-full object-cover"
              />
              {allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevImage}
                    className="absolute start-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                  >
                    <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="absolute end-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
                  >
                    <ChevronRight className="h-5 w-5 rtl:rotate-180" />
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <Building2 className="h-16 w-16" />
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {allImages.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {allImages.map((img, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 ${
                  index === currentImageIndex ? "border-primary" : "border-transparent"
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Badges */}
        <div className="flex gap-2 flex-wrap">
          {getAdTypeBadge()}
          {regaData?.propertyType && (
            <Badge variant="outline">{String(regaData.propertyType)}</Badge>
          )}
        </div>

        {/* Title & Price */}
        <div>
          <h2 className="text-xl font-bold">{advertisementData.title}</h2>
          {regaData?.propertyPrice != null && (
            <p className="text-2xl font-bold text-primary mt-2">
              {formatNumber(getRegaSaleTotal(regaData))}&nbsp;{RIYAL}
            </p>
          )}
        </div>

        {/* Description */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("الوصف", "Description")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {advertisementData.description || t("لا يوجد وصف", "No description")}
            </p>
          </CardContent>
        </Card>

        {/* Property Information */}
        {regaData && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t("معلومات العقار", "Property Information")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label={t("نوع العقار", "Property Type")} value={regaData.propertyType} />
                <ReadOnlyField label={t("المساحة", "Area")} value={regaData.propertyArea != null ? `${regaData.propertyArea} ${t("م²", "m²")}` : null} />
                <ReadOnlyField label={t("عمر العقار", "Property Age")} value={regaData.propertyAge} />
                <ReadOnlyField label={t("واجهة العقار", "Property Face")} value={regaData.propertyFace} />
                <ReadOnlyField label={t("استخدامات العقار", "Usages")} value={Array.isArray(regaData.propertyUsages) ? regaData.propertyUsages.join('، ') : regaData.propertyUsages} />
                <ReadOnlyField label={t("عرض الشارع", "Street Width")} value={regaData.streetWidth ? `${regaData.streetWidth} ${t("م", "m")}` : null} />
                <ReadOnlyField label={t("رقم المخطط", "Plan Number")} value={regaData.planNumber} mono />
                <ReadOnlyField label={t("رقم الأرض", "Land Number")} value={regaData.landNumber} mono />
                <ReadOnlyField label={t("كود البناء السعودي", "Building Code")} value={regaData.complianceWithTheSaudiBuildingCode} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Utilities */}
        {regaData?.propertyUtilities && regaData.propertyUtilities.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                {t("المرافق", "Utilities")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {regaData.propertyUtilities.map((utility, idx) => (
                  <Badge key={idx} variant="secondary">{String(utility)}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right Column */}
      <div className="space-y-4">
        {/* Location */}
        {(loc || (regaData?.location && typeof regaData.location === "string")) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {t("الموقع", "Location")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loc ? (
                <div className="grid grid-cols-2 gap-3">
                  <ReadOnlyField label={t("المنطقة", "Region")} value={loc.region} />
                  <ReadOnlyField label={t("المدينة", "City")} value={loc.city} />
                  <ReadOnlyField label={t("الحي", "District")} value={loc.district} />
                  <ReadOnlyField label={t("الشارع", "Street")} value={loc.street} />
                  <ReadOnlyField label={t("الرمز البريدي", "Postal Code")} value={loc.postalCode} mono />
                  <ReadOnlyField label={t("رقم المبنى", "Building Number")} value={loc.buildingNumber} mono />
                  <ReadOnlyField label={t("الرقم الإضافي", "Additional Number")} value={loc.additionalNumber} mono />
                  {loc.latitude != null && loc.longitude != null && (
                    <ReadOnlyField
                      label={t("الإحداثيات", "Coordinates")}
                      value={`${loc.latitude}, ${loc.longitude}`}
                    />
                  )}
                </div>
              ) : (
                <p className="text-sm">{String(regaData!.location)}</p>
              )}
              {regaData?.locationDescriptionOnMOJDeed && (
                <div className="mt-3 pt-3 border-t">
                  <ReadOnlyField
                    label={t("الوصف حسب الصك", "Location per Deed")}
                    value={regaData.locationDescriptionOnMOJDeed}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Map Placeholder */}
        <Card>
          <CardContent className="p-0">
            <div className="h-40 bg-muted rounded-lg flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MapPin className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">{t("خريطة الموقع", "Location Map")}</p>
                {loc?.latitude != null && loc?.longitude != null && (
                  <p className="text-xs mt-1">{loc.latitude}, {loc.longitude}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Borders */}
        {regaData?.borders && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Compass className="h-4 w-4" />
                {t("الحدود", "Borders")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { dir: t("شمال", "North"), name: regaData.borders.northLimitName, desc: regaData.borders.northLimitDescription, len: regaData.borders.northLimitLengthChar },
                  { dir: t("جنوب", "South"), name: regaData.borders.southLimitName, desc: regaData.borders.southLimitDescription, len: regaData.borders.southLimitLengthChar },
                  { dir: t("شرق", "East"), name: regaData.borders.eastLimitName, desc: regaData.borders.eastLimitDescription, len: regaData.borders.eastLimitLengthChar },
                  { dir: t("غرب", "West"), name: regaData.borders.westLimitName, desc: regaData.borders.westLimitDescription, len: regaData.borders.westLimitLengthChar },
                ].map((border) => {
                  if (!border.name && !border.desc && !border.len) return null;
                  return (
                    <div key={border.dir} className="flex items-start gap-3 text-sm">
                      <span className="font-medium w-12 flex-shrink-0">{border.dir}</span>
                      <div className="flex-1">
                        {border.name && <span>{String(border.name)}</span>}
                        {border.desc && <span className="text-muted-foreground"> — {String(border.desc)}</span>}
                        {border.len && <span className="text-muted-foreground"> ({String(border.len)} {t("م", "m")})</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* License Info */}
        {regaData && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t("معلومات الرخصة", "License Information")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label={t("رقم الرخصة", "License Number")} value={regaData.adLicenseNumber} mono />
                <ReadOnlyField label={t("رقم الصك", "Deed Number")} value={regaData.deedNumber} mono />
                <ReadOnlyField label={t("نوع الصك", "Deed Type")} value={regaData.titleDeedTypeName} />
                <ReadOnlyField label={t("رقم عقد الوساطة", "Brokerage Contract")} value={regaData.brokerageContractNumber || regaData.brokerageAndMarketingLicenseNumber} mono />
                <ReadOnlyField label={t("اسم المعلن", "Advertiser Name")} value={regaData.advertiserName} />
                <ReadOnlyField label={t("نوع المعلن", "Advertiser Type")} value={regaData.advertiserType} />
                <ReadOnlyField label={t("تاريخ الإنشاء", "Creation Date")} value={regaData.creationDate} />
                <ReadOnlyField label={t("تاريخ الانتهاء", "End Date")} value={regaData.endDate} />
                <ReadOnlyField label={t("الموظف المسؤول", "Responsible Employee")} value={regaData.responsibleEmployeeName} />
                <ReadOnlyField label={t("هاتف الموظف", "Employee Phone")} value={regaData.responsibleEmployeePhoneNumber} mono />
                <ReadOnlyField label={t("الهاتف", "Phone")} value={regaData.phoneNumber} mono />
                <ReadOnlyField label={t("مصدر الإعلان", "Ad Source")} value={regaData.adSource} />
              </div>
              {regaData.adLicenseUrl && (
                <div className="mt-3 pt-3 border-t">
                  <a
                    href={String(regaData.adLicenseUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3 rtl:rotate-180" />
                    {t("عرض الرخصة في موقع هيئة العقار", "View license on REGA website")}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Constraints */}
        {regaData?.isConstrained && (
          <Card className="border-amber-300 dark:border-amber-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                {t("القيود", "Constraints")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                <ReadOnlyField label={t("مقيد", "Constrained")} value={regaData.isConstrained ? t("نعم", "Yes") : t("لا", "No")} />
                <ReadOnlyField label={t("التزامات على العقار", "Obligations")} value={regaData.obligationsOnTheProperty} />
                <ReadOnlyField label={t("الضمانات ومدتها", "Guarantees")} value={regaData.guaranteesAndTheirDuration} />
                <ReadOnlyField label={t("نوع رسوم نقل الملكية", "Transfer Fee Type")} value={regaData.ownershipTransferFeeType} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {regaData?.notes && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t("ملاحظات", "Notes")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{String(regaData.notes)}</p>
            </CardContent>
          </Card>
        )}

        {/* Internal Linking (subtle) */}
        {(unitData || propertyData) && (
          <Card className="border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="h-3 w-3" />
                {t("ربط داخلي", "Internal Linking")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground">
              <div className="grid grid-cols-2 gap-2">
                {propertyData && (
                  <div>
                    <span>{t("العقار", "Property")}</span>
                    <p className="font-medium text-foreground">{propertyData.name}</p>
                  </div>
                )}
                {unitData && (
                  <div>
                    <span>{t("الوحدة", "Unit")}</span>
                    <p className="font-medium text-foreground">{t("وحدة", "Unit")} {unitData.unit_number}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non-constrained info */}
        {regaData && !regaData.isConstrained && (regaData.guaranteesAndTheirDuration || regaData.ownershipTransferFeeType) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                {t("ضمانات ورسوم", "Guarantees & Fees")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                <ReadOnlyField label={t("الضمانات ومدتها", "Guarantees")} value={regaData.guaranteesAndTheirDuration} />
                <ReadOnlyField label={t("نوع رسوم نقل الملكية", "Transfer Fee Type")} value={regaData.ownershipTransferFeeType} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
